import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import AuthService from '../../services/AuthService';
import { formatDateLocal, calculateAge, formatNumberWithCommas, parseNumberFromFormatted } from '../../utils/helper';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Dropdown } from 'primereact/dropdown';

const AddUpdate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoader, setIsLoader] = useState(false);
    const [patients, setPatients] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [checkupTypes, setCheckupTypes] = useState([]);
    const [insuranceTypes, setInsuranceTypes] = useState([]);
    const [currency, setCurrency] = useState(process.env.REACT_APP_CURRENCY || 'TZS');
    
    // New/Existing Patient Logic
    const [patientType, setPatientType] = useState('existing'); // 'existing' or 'new'
    const [newPatientData, setNewPatientData] = useState({
        name: '',
        phone: '',
        email: '',
        gender: 'male',
        address: '',
        date_of_birth: '',
        insurance_type_id: '',
        insurance_card_number: ''
    });
    const [phoneCode, setPhoneCode] = useState('+255');


    const [formData, setFormData] = useState({
        patient_id: '',
        doctor_id: '',
        checkup_type_id: '',
        date: new Date(),
        time: '',
        status: 'queue',
        notes: '',
        ref_no: '',
        token_no: '',
        price: '',
        amount_paid: '',
        payment_status: 'pending',
        payment_method: ''
    });

    const authUser = AuthService.getCurrentUser();
    const isAdmin = authUser?.role === 'admin';

    useEffect(() => {
        getPatients();
        getDoctors();
        getCheckupTypes();
        getInsuranceTypes();
        if (id) {
            getSingleData();
        }
    }, [id]);

    const getPatients = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/patients',
                data: { per_page: 1000 } // Get all patients for dropdown
            });
            if (response.data.status) {
                // Add label for dropdown
                const formattedPatients = (response.data.data.patients || []).map(p => ({
                    ...p,
                    label: `${p.name} (${p.phone})`
                }));
                setPatients(formattedPatients);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getDoctors = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/users',
                data: { role: 'doctor', per_page: 1000 } // Get all doctors
            });
            if (response.data.status) {
                setDoctors(response.data.data.users || []);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getCheckupTypes = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/checkup-types/all'
            });
            if (response.data.status) {
                setCheckupTypes(response.data.data || []);
                // Only set default if creating new appointment
                if (!id) {
                    setFormData(prev => ({
                        ...prev,
                        checkup_type_id: response.data.data[0]?.id || '',
                        price: response.data.data[0]?.price || ''
                    }));
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getInsuranceTypes = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/insurance-types`,
                data:{per_page:100}
            });
            if (response.data.status) {
                setInsuranceTypes(response.data.data.data || []);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load insurance types');
        }
    };

    const getSingleData = async () => {
        setIsLoader(true);
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/appointments/${id}`,
            });
            const data = response.data;
            if (data.status) {
                const appt = data.data;
                setFormData({
                    patient_id: appt.patient_id,
                    doctor_id: appt.doctor_id,
                    checkup_type_id: appt.checkup_type_id,
                    date: new Date(appt.date),
                    time: appt.time,
                    status: appt.status,
                    notes: appt.notes || '',
                    ref_no: appt.ref_no,
                    token_no: appt.token_no,
                    price: appt.price || '',
                    amount_paid: appt.amount_paid || '',
                    payment_status: appt.payment_status || 'pending',
                    payment_method: appt.payment_method || ''
                });
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to load appointment data');
        } finally {
            setIsLoader(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let newValue = value;

        if (name === 'price' || name === 'amount_paid') {
            newValue = parseNumberFromFormatted(newValue);
        }

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
    };

    const handleDateChange = (date) => {
        setFormData(prev => ({
            ...prev,
            date: date
        }));
    };

    const handleCheckupTypeSelect = (typeId, price) => {
        // Recalculate payment status if price changes
        const pPrice = parseFloat(price) || 0;
        const paid = parseFloat(formData.amount_paid) || 0;
        let pStatus = 'pending';
        if (paid >= pPrice && pPrice > 0) pStatus = 'completed';
        else if (paid > 0) pStatus = 'partially_paid';

        setFormData(prev => ({
            ...prev,
            checkup_type_id: typeId,
            price: price || '',
            payment_status: pStatus
        }));
    };

    const handleNewPatientChange = (e) => {
        const { name, value } = e.target;
        setNewPatientData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const createPatient = async () => {
        try {
            const fullPhone = phoneCode + newPatientData.phone;
            const payload = {
                ...newPatientData,
                phone: fullPhone,
                status: 'true'
            };

            const response = await ApiService.request({
                method: 'POST',
                url: 'admin/patients',
                data: payload
            });

            if (response.data.status) {
                return response.data.data.id; // Assuming API returns created patient object with id
            } else {
                toast.error(response.data.message || "Failed to create patient");
                return null;
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Failed to create patient');
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        let finalPatientId = formData.patient_id;

        if (patientType === 'new') {
             // Validate new patient data
             if (!newPatientData.name || !newPatientData.phone) {
                 toast.error("Please fill required patient fields (Name, Phone)");
                 return;
             }
             
             setIsLoader(true);
             const newId = await createPatient();
             if (!newId) {
                 setIsLoader(false);
                 return;
             }
             finalPatientId = newId;

             // Switch to existing mode with the new patient selected to prevent duplicate creation on retry
             setPatientType('existing');
             setFormData(prev => ({...prev, patient_id: newId}));
             
             // Add new patient to the dropdown list
             const newPatientOption = {
                 id: newId,
                 label: `${newPatientData.name} (${newPatientData.phone})`,
                 name: newPatientData.name,
                 phone: newPatientData.phone
             };
             setPatients(prev => [...prev, newPatientOption]);
        } else {
            if (!finalPatientId) {
                toast.error("Please select a patient");
                return;
            }
        }

        if (!formData.doctor_id || !formData.date || !formData.time) {
            toast.error("Please fill all required fields");
            setIsLoader(false);
            return;
        }

        setIsLoader(true);
        try {
            const payload = {
                ...formData,
                patient_id: finalPatientId,
                date: formatDateLocal(formData.date) // Format YYYY-MM-DD
            };

            const response = await ApiService.request({
                method: id ? 'PUT' : 'POST',
                url: id ? `admin/appointments/${id}` : 'admin/appointments',
                data: payload,
            });

            if (response.data.status) {
                toast.success(response.data.message);
                navigate('/appointments');
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Something went wrong');
        } finally {
            setIsLoader(false);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            {id ? `Edit Appointment (Ref: ${formData.ref_no || ''})` : 'Create Appointment'}
                        </h1>
                        {formData.token_no && (
                             <span className="badge badge-light-primary fw-bold fs-7 ms-2">Token: {formData.token_no}</span>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <Link to="/appointments" className="btn btn-sm fw-bold btn-secondary">
                            Back
                        </Link>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-body py-4">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6 mb-5">
                                        <label className="form-label fs-5 fw-bold mb-3">Patient Details</label>
                                        <div className="d-flex gap-4 mb-3">
                                            <div className="form-check">
                                                <input 
                                                    className="form-check-input" 
                                                    type="radio" 
                                                    name="patientType"
                                                    id="existingPatient"
                                                    checked={patientType === 'existing'} 
                                                    onChange={() => setPatientType('existing')} 
                                                />
                                                <label className="form-check-label" htmlFor="existingPatient">Existing Patient</label>
                                            </div>
                                            <div className="form-check">
                                                <input 
                                                    className="form-check-input" 
                                                    type="radio" 
                                                    name="patientType"
                                                    id="newPatient"
                                                    checked={patientType === 'new'} 
                                                    onChange={() => {
                                                        setPatientType('new');
                                                        setFormData(prev => ({...prev, patient_id: ''}));
                                                    }} 
                                                />
                                                <label className="form-check-label" htmlFor="newPatient">New Patient</label>
                                            </div>
                                        </div>

                                        {patientType === 'existing' ? (
                                            <div className=" mb-3">
                                                <label className="form-label required">Select Patient</label>
                                                <Dropdown 
                                                    value={formData.patient_id} 
                                                    options={patients} 
                                                    onChange={(e) => setFormData({...formData, patient_id: e.value})} 
                                                    optionLabel="label" 
                                                    optionValue="id"
                                                    filter 
                                                    showClear
                                                    filterBy="label"
                                                    placeholder="Select a Patient" 
                                                    className="w-100"
                                                />
                                                
                                                {formData.patient_id && (
                                                    <div className="card mt-3 border border-dashed border-primary bg-light-primary">
                                                        <div className="card-body p-3">
                                                            <div className="d-flex align-items-center mb-2">
                                                                <i className="fas fa-user-check text-primary fs-3 me-2"></i>
                                                                <h5 className="card-title text-primary mb-0">Selected Patient Details</h5>
                                                            </div>
                                                            {(() => {
                                                                const selected = patients.find(p => p.id === formData.patient_id);
                                                                if (!selected) return null;
                                                                return (
                                                                    <div className="row fs-6 text-gray-700">
                                                                        <div className="col-md-6 mb-1">
                                                                            <span className="fw-bold text-gray-900">Name:</span> {selected.name}
                                                                        </div>
                                                                        <div className="col-md-6 mb-1">
                                                                            <span className="fw-bold text-gray-900">Phone:</span> {selected.phone}
                                                                        </div>
                                                                        <div className="col-md-6 mb-1">
                                                                            <span className="fw-bold text-gray-900">Gender:</span> {selected.gender || 'N/A'}
                                                                        </div>
                                                                        <div className="col-md-6 mb-1">
                                                                            <span className="fw-bold text-gray-900">Age:</span> {calculateAge(selected.date_of_birth) || 'N/A'}
                                                                        </div>
                                                                        <div className="col-md-6 mb-1">
                                                                            <span className="fw-bold text-gray-900">Insurance Type:</span> {selected.insurance_type?.title || 'N/A'}
                                                                        </div>
                                                                        <div className="col-md-6 mb-1">
                                                                            <span className="fw-bold text-gray-900">Insurance Number:</span> {selected.insurance_card_number || 'N/A'}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="row p-4 border rounded shadow">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label required">Name</label>
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-solid" 
                                                        name="name"
                                                        value={newPatientData.name}
                                                        onChange={handleNewPatientChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label required">Phone</label>
                                                     <div className="input-group">
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-solid w-80px" 
                                                            value={phoneCode}
                                                            onChange={(e) => setPhoneCode(e.target.value)}
                                                            placeholder="+255"
                                                            style={{ maxWidth: '80px' }}
                                                        />
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-solid" 
                                                            name="phone"
                                                            value={newPatientData.phone}
                                                            onChange={(e) => {
                                                                const val = e.target.value.replace(/\D/g, '');
                                                                handleNewPatientChange({ target: { name: 'phone', value: val } });
                                                            }}
                                                            placeholder="Phone Number"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                                
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Insurance Type</label>
                                                    <select 
                                                        className="form-select form-select-solid" 
                                                        name="insurance_type_id"
                                                        value={newPatientData.insurance_type_id}
                                                        onChange={handleNewPatientChange}
                                                    >
                                                        <option value="">Select Insurance Type</option>
                                                        {insuranceTypes.map((type) => (
                                                            <option key={type.id} value={type.id}>{type.title}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                {newPatientData.insurance_type_id && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label required">Insurance Card Number</label>
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-solid" 
                                                            name="insurance_card_number"
                                                            value={newPatientData.insurance_card_number}
                                                            onChange={handleNewPatientChange}
                                                            required
                                                        />
                                                    </div>
                                                )}

                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Email</label>
                                                    <input 
                                                        type="email" 
                                                        className="form-control form-control-solid" 
                                                        name="email"
                                                        value={newPatientData.email}
                                                        onChange={handleNewPatientChange}
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Gender</label>
                                                    <select 
                                                        className="form-select form-select-solid" 
                                                        name="gender"
                                                        value={newPatientData.gender}
                                                        onChange={handleNewPatientChange}
                                                    >
                                                        <option value="male">Male</option>
                                                        <option value="female">Female</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Date of Birth</label>
                                                    <input 
                                                        type="date" 
                                                        className="form-control form-control-solid" 
                                                        name="date_of_birth"
                                                        value={newPatientData.date_of_birth}
                                                        onChange={handleNewPatientChange}
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Address</label>
                                                    <textarea 
                                                        className="form-control form-control-solid" 
                                                        name="address"
                                                        rows="1"
                                                        value={newPatientData.address}
                                                        onChange={handleNewPatientChange}
                                                    ></textarea>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="col-md-6"></div>

                                    <div className="col-md-6 mb-3 margin-doctor" >
                                        <label className="form-label required">Doctor</label>
                                        <div className="w-100">
                                            <Dropdown 
                                                value={formData.doctor_id} 
                                                options={doctors} 
                                                onChange={(e) => setFormData({...formData, doctor_id: e.value})} 
                                                optionLabel="name" 
                                                optionValue="id"
                                                filter 
                                                placeholder="Select a Doctor" 
                                                className="w-100"
                                            />
                                        </div>
                                    </div>

                                    {/* Checkup Types Selection */}
                                    <div className="col-12 mb-5">
                                        <label className="form-label fs-5 fw-bold mb-3">Select Checkup Type</label>
                                        <div className="row g-3">
                                            {checkupTypes.map((type) => (
                                                <div className="col-md-3 col-sm-6" key={type.id}>
                                                    <div 
                                                        className={`card h-100 cursor-pointer ${formData.checkup_type_id === type.id ? 'bg-base2 text-white border border-2' : 'bg-light hover-elevate-up border border-2'}`}
                                                        onClick={() => handleCheckupTypeSelect(type.id, type.price)}
                                                        style={{transition: 'all 0.3s'}}
                                                    >
                                                        <div className="card-body p-4 text-center d-flex flex-column justify-content-center">
                                                            <div className="fs-4 fw-bold mb-1">{type.title}</div>
                                                            <div className={`fs-6 ${formData.checkup_type_id === type.id ? 'text-white' : 'text-muted'}`}>
                                                               {currency} {type.price}
                                                            </div>
                                                            
                                                            {formData.checkup_type_id === type.id && (
                                                                <div className="mt-2">
                                                                    <i className="fas fa-check-circle text-white fs-2"></i>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {checkupTypes.length === 0 && (
                                                <div className="text-muted">No checkup types available. Please add them in Data Management.</div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Price Override for Admin */}
                                    {isAdmin && formData.checkup_type_id && (
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Checkup Price</label>
                                            <div className="input-group input-group-solid">
                                                <span className="input-group-text">{currency}</span>
                                                <input 
                                                    type="text" 
                                                    className="form-control form-control-solid" 
                                                    name="price"
                                                    value={formatNumberWithCommas(formData.price)}
                                                    onChange={handleChange}
                                                    placeholder="Enter custom price"
                                                />
                                            </div>
                                            <div className="form-text">Original Price: {currency} {checkupTypes.find(t => t.id === formData.checkup_type_id)?.price || 0}</div>
                                        </div>
                                    )}

                                    <div className="col-md-3 mb-3">
                                        <label className="form-label required">Date</label>
                                        <div className="w-100">
                                            <DatePicker
                                                selected={formData.date}
                                                onChange={handleDateChange}
                                                className="form-control form-control-solid "
                                                dateFormat="yyyy-MM-dd"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-3 mb-3">
                                        <label className="form-label required">Time</label>
                                        <input
                                            type="time"
                                            name="time"
                                            className="form-control form-control-solid"
                                            value={formData.time}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Amount Paid</label>
                                        <div className="input-group input-group-solid">
                                            <span className="input-group-text">{currency}</span>
                                            <input
                                                type="text"
                                                name="amount_paid"
                                                className="form-control form-control-solid"
                                                value={formatNumberWithCommas(formData.amount_paid)}
                                                onChange={(e) => {
                                                    const numericVal = parseNumberFromFormatted(e.target.value);
                                                    const val = parseFloat(numericVal) || 0;
                                                    const price = parseFloat(formData.price) || 0;
                                                    let pStatus = 'pending';
                                                    if (val >= price && price > 0) pStatus = 'completed';
                                                    else if (val > 0) pStatus = 'partially_paid';
                                                    
                                                    setFormData(prev => ({ ...prev, amount_paid: numericVal, payment_status: pStatus }));
                                                }}
                                                placeholder="0.00"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Payment Method</label>
                                        <select
                                            name="payment_method"
                                            className="form-control form-control-solid"
                                            value={formData.payment_method}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Method</option>
                                            <option value="cash">Cash</option>
                                            <option value="card">Card</option>
                                            <option value="mobile_money">Mobile Money</option>
                                            <option value="insurance">Insurance</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Payment Status</label>
                                        <input
                                            type="text"
                                            className="form-control form-control-solid bg-secondary"
                                            value={formData.payment_status?.replace('_', ' ').toUpperCase()}
                                            readOnly
                                        />
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <label className="form-label">Notes</label>
                                        <textarea
                                            name="notes"
                                            className="form-control form-control-solid"
                                            rows="3"
                                            value={formData.notes}
                                            onChange={handleChange}
                                        ></textarea>
                                    </div>
                                </div>

                                <div className="text-end">
                                    <button type="submit" className="btn btn-base" disabled={isLoader}>
                                        {isLoader ? 'Saving...' : 'Save Appointment'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddUpdate;
